import { useCallback, useEffect, useState } from 'react'
import { PartNotFoundError } from './types'
import type { PartActivityEvent, PartDetail } from './types'
import { defaultPartsLibraryService } from './usePartsLibrary'
import type { PartsLibraryService } from './usePartsLibrary'

export type PartDetailState =
  | { readonly status: 'loading' }
  | { readonly status: 'not_found' }
  | { readonly status: 'error'; readonly message: string }
  | {
      readonly status: 'ready'
      readonly part: PartDetail
      readonly activity: readonly PartActivityEvent[]
    }

export interface UsePartDetailResult {
  readonly state: PartDetailState
  readonly refetch: () => void
}

/**
 * Loads a single Part detail (UID-45, screens.md §6) through the same
 * `PartsLibraryService` seam as the Parts Library screen. `not_found` is a
 * distinct state from `error` — the contract's "tenant-scoped 404" is a
 * navigable dead-end (bad link/unauthorized), not a transient failure, so
 * the screen shows a "Part not found" empty state rather than a retry CTA.
 */
export function usePartDetail(
  partId: string,
  service: PartsLibraryService = defaultPartsLibraryService,
): UsePartDetailResult {
  const [state, setState] = useState<PartDetailState>({ status: 'loading' })
  const [generation, setGeneration] = useState(0)

  // `generation` is a manual re-fetch trigger (bumped by `refetch`) that
  // isn't read in the effect body — it exists purely to force a re-run.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above.
  useEffect(() => {
    let cancelled = false
    setState({ status: 'loading' })

    service
      .getPartDetail(partId)
      .then((part) => {
        if (!cancelled) setState({ status: 'ready', part, activity: part.activity })
      })
      .catch((error: unknown) => {
        if (cancelled) return
        if (error instanceof PartNotFoundError) {
          setState({ status: 'not_found' })
          return
        }
        setState({
          status: 'error',
          message:
            error instanceof Error ? error.message : 'We couldn’t load this part. Try again.',
        })
      })

    return () => {
      cancelled = true
    }
  }, [partId, service, generation])

  const refetch = useCallback(() => setGeneration((g) => g + 1), [])

  return { state, refetch }
}
